import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AsyncMultiSelectDropdown,
  DateInput,
  Dropdown,
  FormTemplate,
  IfFormData,
  RadioButtonsGroup,
  Row6x6x,
  SubmitButton,
} from 'ui-builder';
import {
  AdditionalWorkflowDateOption,
  FileType,
  ProjectType,
  UpdateProjectDatesOption,
  useAdditionalWorkflowAiService,
  usePlansService,
  useProjectPlanService,
} from 'nekst-api';
import {
  FileUploaderInput,
  usePopupFormManager,
} from 'features/nekst-widgets';
import { useProjectContext } from '../../view/ProjectContext';
import { useDataLoaderNew } from 'data-loader';
import { AiAnalysisLoaderWidget } from '../Shared/AiAnalysisLoaderWidget';

type WorkflowMode = 'manual' | 'ai';

function WorkflowMultiDropdown(props: {
  projectType: ProjectType;
  setupAreaId: number;
}) {
  const plansService = usePlansService();

  return (
    <AsyncMultiSelectDropdown
      source="plansIds"
      label="Select Workflow(s)"
      loadOptionsFunc={async () => {
        const plans = await plansService.getAllTeamPlans(props.projectType, props.setupAreaId);
        return plans.map((plan) => ({
          value: plan.id,
          label: plan.name,
        }));
      }}
    />
  );
}

const DATE_OPTION_OPTIONS = [
  {
    value: AdditionalWorkflowDateOption.USE_TRANSACTION_DATES,
    label: 'Use Transaction Start and End Date',
  },
  {
    value: AdditionalWorkflowDateOption.UPDATE_TRANSACTION_DATES,
    label: 'Update Transaction Dates',
  },
];

function DateOptionDropdown() {
  return (
    <Dropdown<AdditionalWorkflowDateOption>
      source="dateOption"
      label="How should task dates be calculated?"
      options={DATE_OPTION_OPTIONS}
    />
  );
}

const EXISTING_TASKS_DATES_OPTIONS = [
  {
    value: UpdateProjectDatesOption.DO_NOT_CHANGE_DATES,
    label: 'Do Not Change Existing Task Dates',
  },
  {
    value: UpdateProjectDatesOption.ADJUST_TASKS_DATES,
    label: 'Adjust Existing Task Dates',
  },
];

function ExistingTasksDatesOptionDropdown() {
  return (
    <Dropdown<UpdateProjectDatesOption>
      source="existingTasksDatesOption"
      label="How should existing task dates be handled?"
      options={EXISTING_TASKS_DATES_OPTIONS}
    />
  );
}

interface WorkflowSelectionFormData {
  mode: WorkflowMode;
  plansIds: number[];
  fileId?: number;
  dateOption: AdditionalWorkflowDateOption;
  customStartDate?: Date;
  customEndDate?: Date;
  existingTasksDatesOption: UpdateProjectDatesOption;
}

const isUpdateTransactionDates = (data: WorkflowSelectionFormData) =>
  data.dateOption === AdditionalWorkflowDateOption.UPDATE_TRANSACTION_DATES;

const isAiMode = (data: WorkflowSelectionFormData) => data.mode === 'ai';

export default function WorkflowSelectionModal(props: { projectId: number }) {
  const projectPlanService = useProjectPlanService();
  const additionalWorkflowAiService = useAdditionalWorkflowAiService();
  const popupFormManager = usePopupFormManager();
  const navigate = useNavigate();
  const projectContext = useProjectContext();
  const plansService = usePlansService();

  const projectType = projectContext.data?.type;
  const setupAreaId = projectContext.data?._setupArea?.id;

  const { data: allPlans } = useDataLoaderNew(async () => {
    if (!projectType || !setupAreaId) {
      return [];
    }
    return plansService.getAllTeamPlans(projectType, setupAreaId);
  }, { dependencies: [projectType, setupAreaId] });

  const aiPlans = (allPlans || []).filter((p) => p.isLaunchingFromContractSupported);
  const hasAiPlans = aiPlans.length > 0;

  const submitFunc = async (data: WorkflowSelectionFormData) => {
    const createdPlan = await projectPlanService.addNewPlansToProject(
      props.projectId,
      data.plansIds,
      data.dateOption,
      isUpdateTransactionDates(data) ? data.customStartDate : undefined,
      isUpdateTransactionDates(data) ? data.customEndDate : undefined,
      isUpdateTransactionDates(data) ? data.existingTasksDatesOption : undefined,
    );

    if (data.mode === 'ai') {
      if (!data.fileId) {
        throw new Error('fileId is required for AI mode');
      }

      await additionalWorkflowAiService.analyzeContract(
        props.projectId,
        createdPlan.id,
        data.fileId,
      );

      popupFormManager.closePopup();
      navigate(`/transactions/${props.projectId}/plans/${createdPlan.id}/ailaunch/verifydata`);
    } else {
      popupFormManager.closePopup();
      const firstStep = createdPlan.hasGroups ? 'groups' : 'keydates';
      navigate(`/transactions/${props.projectId}/plans/${createdPlan.id}/launch/${firstStep}`);
    }
  };

  if (!projectType || !setupAreaId) {
    return null;
  }

  return (
    <FormTemplate<WorkflowSelectionFormData>
      submitFormFunc={submitFunc}
      initialData={{
        mode: 'manual' as WorkflowMode,
        plansIds: [],
        dateOption: AdditionalWorkflowDateOption.USE_TRANSACTION_DATES,
        existingTasksDatesOption: UpdateProjectDatesOption.DO_NOT_CHANGE_DATES,
      }}
      title="Add Workflow"
      buttons={<SubmitButton text="Continue" />}
    >
      {hasAiPlans && (
        <RadioButtonsGroup<string>
          source="mode"
          label="How would you like to add the workflow?"
          options={[
            { value: 'manual', label: 'Manually' },
            { value: 'ai', label: 'With AI' },
          ]}
        />
      )}
      <WorkflowMultiDropdown
        projectType={projectType}
        setupAreaId={setupAreaId}
      />
      <DateOptionDropdown />
      <IfFormData<WorkflowSelectionFormData> conditionFunc={isUpdateTransactionDates}>
        <Row6x6x>
          <DateInput source="customStartDate" label="Start Date" />
          <DateInput source="customEndDate" label="End Date" />
        </Row6x6x>
      </IfFormData>
      <IfFormData<WorkflowSelectionFormData> conditionFunc={isUpdateTransactionDates}>
        <ExistingTasksDatesOptionDropdown />
      </IfFormData>
      <IfFormData<WorkflowSelectionFormData> conditionFunc={isAiMode}>
        <FileUploaderInput
          fileType={FileType.PROJECT_DOCUMENT}
          fileDataSource="_file"
          source="fileId"
          label="Contract Document"
        />
        <AiAnalysisLoaderWidget />
      </IfFormData>
    </FormTemplate>
  );
}
